import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { EASE_OUT_QUART } from "@/lib/motion";

/**
 * Shared step-body transition for the setup wizard. Lives here so the
 * individual step files stay declarative — they render their form and the
 * shell wraps them in an `<AnimatePresence mode="wait">` keyed by step id.
 *
 * Enter  = fade + 8px rise, ~200ms ease-out-quart.
 * Exit   = faster fade (~120ms), no translate — exits should feel snappier
 *          than entrances so the incoming step doesn't wait on a long exit.
 * Motion  is `transform`/`opacity` only (never width/height/layout props).
 *
 * All motion is gated behind `useReducedMotion()`: when the user prefers
 * reduced motion we drop the translate entirely and fall back to a short
 * opacity fade so the transition is essentially instant.
 */

export function StepTransition({ children }: { children: ReactNode }) {
	const reduce = useReducedMotion();

	return (
		<motion.div
			initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
			animate={{
				opacity: 1,
				y: 0,
				transition: reduce
					? { duration: 0.12 }
					: { duration: 0.22, ease: EASE_OUT_QUART },
			}}
			exit={{
				opacity: 0,
				transition: { duration: reduce ? 0.08 : 0.12, ease: "easeOut" },
			}}
		>
			{children}
		</motion.div>
	);
}
